import { useCallback } from "react"
import type {
  ProposalBatch,
  Proposal,
} from "@stockline/shared/types/ai/proposal"
import { PROPOSAL_BATCH_TABLE } from "../../../lib/offline/resources/ai/proposal-batch/db"
import { useAISync } from "../providers/AISyncProvider"

const PUSH_ENDPOINT = "/api/v2/ai-proposals/changes"
const BATCH_ENDPOINT = "/api/v2/ai-proposals"

/**
 * Thrown when the server applied the batch transition but it ended up in
 * `failed` status (e.g. a Python `modify_confirmed_pre_order` rejected the
 * change). Carries the server-provided `failureReason` so the UI can surface it.
 */
export class ProposalActionFailedError extends Error {
  constructor(
    public readonly batchId: string,
    public readonly failureReason: string | null
  ) {
    super(failureReason ?? "Proposal action failed")
    this.name = "ProposalActionFailedError"
  }
}

/**
 * Mutations for proposal batches: approve, reject, edit, remove, add proposals.
 *
 * Approve/reject: synchronous — posts directly to the V2 API and re-reads the
 * actual server state by id. No optimistic write — the UI shows a loader until
 * the server confirms the outcome. Required because an AI proposal on a
 * CONFIRMED pre-order is delegated to Python's `modify_confirmed_pre_order`
 * (which calls the ERP); the result (approved / failed) cannot be predicted
 * client-side.
 *
 * Edit/remove/add: optimistic — immediately update Dexie then enqueue a
 * change. SSE `proposal-batch.updated` reconciles. Used for inline editing
 * of pending batches before approval.
 */
export function useProposalMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const submit = useCallback(
    async (
      id: string,
      status: "approved" | "rejected"
    ): Promise<ProposalBatch> => {
      const existing = await storage.get<ProposalBatch>(
        PROPOSAL_BATCH_TABLE,
        id
      )
      if (!existing) {
        throw new ProposalActionFailedError(id, "Proposal not found locally")
      }
      if (existing.status !== "pending") return existing

      await engine.http.post(PUSH_ENDPOINT, {
        changes: [{ op: "update", id, data: { status } }],
        clientSyncId: 0,
      })

      // Re-read the actual server state — the push response is a paged sync
      // result and may not include this batch when many newer changes exist.
      const settled = await engine.http.get<ProposalBatch>(
        `${BATCH_ENDPOINT}/${id}`
      )
      await storage.put(PROPOSAL_BATCH_TABLE, settled)

      if (settled.status === "failed") {
        throw new ProposalActionFailedError(id, settled.failureReason)
      }
      return settled
    },
    [storage, engine]
  )

  const approveProposal = useCallback(
    async (id: string, _reviewerUserId: string): Promise<void> => {
      await submit(id, "approved")
    },
    [submit]
  )

  const rejectProposal = useCallback(
    async (id: string, _reviewerUserId: string): Promise<void> => {
      await submit(id, "rejected")
    },
    [submit]
  )

  const editProposal = useCallback(
    async (
      batchId: string,
      proposalId: string,
      payloadUpdates: Record<string, unknown>
    ): Promise<void> => {
      const existing = await storage.get<ProposalBatch>(
        PROPOSAL_BATCH_TABLE,
        batchId
      )
      if (!existing || existing.status !== "pending") return

      const proposal = existing.proposals.find((p) => p.id === proposalId)
      if (!proposal) return

      // Skip enqueueing edits against an optimistically-created flow proposal
      // whose temp id hasn't yet been replaced by the server's real id via
      // SSE. Otherwise the server would receive `update-proposal` for an id
      // it doesn't know about and reject it.
      if ((proposal as Proposal & { __optimistic?: boolean }).__optimistic) {
        console.warn(
          "[useProposalMutations] skipping update-proposal against optimistic proposal id",
          proposalId
        )
        return
      }

      const updated: ProposalBatch = {
        ...existing,
        updatedAt: new Date().toISOString(),
        proposals: existing.proposals.map((p) =>
          p.id === proposalId
            ? {
                ...p,
                payload: { ...p.payload, ...payloadUpdates },
                updatedAt: new Date().toISOString(),
              }
            : p
        ),
      }

      await storage.put(PROPOSAL_BATCH_TABLE, updated)
      // Custom op types — server validates with its own Zod schema
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await changeQueue.enqueue("proposal-batches", {
        op: "update-proposal",
        id: proposalId,
        batchId,
        data: { payload: payloadUpdates },
      } as any)
      void engine.pushChanges("proposal-batches")
    },
    [storage, changeQueue, engine]
  )

  const removeProposal = useCallback(
    async (batchId: string, proposalId: string): Promise<void> => {
      const existing = await storage.get<ProposalBatch>(
        PROPOSAL_BATCH_TABLE,
        batchId
      )
      if (!existing || existing.status !== "pending") return

      const proposal = existing.proposals.find((p) => p.id === proposalId)
      // Mirror the editProposal guard — bail out when the proposal id doesn't
      // exist in the batch (e.g. already removed by SSE reconciliation).
      if (!proposal) return

      // Same optimistic-id guard as editProposal: a temp-id proposal hasn't
      // been persisted yet, so a server-side delete would target a missing id.
      if ((proposal as Proposal & { __optimistic?: boolean }).__optimistic) {
        console.warn(
          "[useProposalMutations] skipping delete-proposal against optimistic proposal id",
          proposalId
        )
        return
      }

      const updated: ProposalBatch = {
        ...existing,
        updatedAt: new Date().toISOString(),
        proposals: existing.proposals.filter((p) => p.id !== proposalId),
      }

      await storage.put(PROPOSAL_BATCH_TABLE, updated)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await changeQueue.enqueue("proposal-batches", {
        op: "delete-proposal",
        id: proposalId,
        batchId,
      } as any)
      void engine.pushChanges("proposal-batches")
    },
    [storage, changeQueue, engine]
  )

  // Generic optimistic "add a proposal to a pending batch". Used for any
  // entityType (a new client-group price line, a flow, etc.). The
  // `__optimistic` marker lets editProposal/removeProposal skip a temp id
  // until SSE reconciles the server's real id back into Dexie.
  const addProposal = useCallback(
    async (
      batchId: string,
      spec: {
        entityType: string
        payload: Record<string, unknown>
        operation?: "create" | "update" | "delete"
      }
    ): Promise<void> => {
      const existing = await storage.get<ProposalBatch>(
        PROPOSAL_BATCH_TABLE,
        batchId
      )
      if (!existing || existing.status !== "pending") return

      const operation = spec.operation ?? "create"
      const tempId = crypto.randomUUID()
      const maxSequence = Math.max(
        ...existing.proposals.map((p) => p.sequence),
        -1
      )

      const newProposal = {
        id: tempId,
        batchId,
        companyId: existing.companyId,
        sequence: maxSequence + 1,
        operation,
        entityType: spec.entityType,
        entityId: null,
        payload: spec.payload,
        resultEntityId: null,
        syncId: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: null,
        __optimistic: true,
      } as Proposal & { __optimistic: true }

      const updated: ProposalBatch = {
        ...existing,
        updatedAt: new Date().toISOString(),
        proposals: [...existing.proposals, newProposal],
      }

      await storage.put(PROPOSAL_BATCH_TABLE, updated)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await changeQueue.enqueue("proposal-batches", {
        op: "create-proposal",
        batchId,
        data: { operation, entityType: spec.entityType, payload: spec.payload },
      } as any)
      void engine.pushChanges("proposal-batches")
    },
    [storage, changeQueue, engine]
  )

  const addFlowProposal = useCallback(
    (batchId: string, flowPayload: Record<string, unknown>): Promise<void> =>
      addProposal(batchId, {
        entityType: "pre_order_flow",
        payload: flowPayload,
      }),
    [addProposal]
  )

  return {
    approveProposal,
    rejectProposal,
    editProposal,
    removeProposal,
    addProposal,
    addFlowProposal,
  }
}
